import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  User as FirebaseUser,
} from 'firebase/auth'
import { doc, setDoc, getDoc } from 'firebase/firestore'
import { auth, db } from './config'
import { User } from '@/types/user.types'
import { AuthUser } from '@/types/auth.types'
import { setupInstitutionalUser, setAttendantClaims } from '@/utils/functionsCaller'
import { getColombianTimestamp } from './firestore.service'

const googleProvider = new GoogleAuthProvider()


interface RegisterParams {
  email: string
  password: string
  userData: Omit<User, 'documentId' | 'createdAt' | 'email'>
}

export const authService = {
  register: async ({ email, password, userData }: RegisterParams) => {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    await updateProfile(userCredential.user, { displayName: userData.displayName })

    if (email.endsWith('@jecaro.co') || email.endsWith('@jecaro.edu.co')) {
      await signOut(auth);
      throw new Error("Debe usar un correo que no sea institucional");
    }

    const _userDocRef = doc(db, 'attendants', userCredential.user.uid)
    const _userDoc = await getDoc(_userDocRef)

    if (_userDoc.exists()) {
      await signOut(auth);
      throw new Error('Esta cuenta ya está registrada');
    }

    const tokenResult = await userCredential.user.getIdTokenResult(true);

    let role = tokenResult.claims.role;
    if (!tokenResult.claims.role || tokenResult.claims.role === "") {
      await setAttendantClaims({ uid: userCredential.user.uid });
      const newToken = await userCredential.user.getIdTokenResult(true);
      role = newToken.claims.role as string
    }

    // Create user document in Firestore with the UID as document ID
    const userDoc: any = {
      ...userData,
      email: email,
      createdAt: getColombianTimestamp(),
    }

    const userDocRef = doc(db, "attendants", userCredential.user.uid)
    await setDoc(userDocRef, userDoc)

    const authUser = {
      ...userCredential.user,
      role: role
    } as unknown as AuthUser
    return authUser
  },

  login: async (email: string, password: string): Promise<AuthUser> => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    const tokenResult = await userCredential.user.getIdTokenResult(true)

    if (userCredential.user.email?.endsWith('@jecaro.co') || userCredential.user.email?.endsWith('@jecaro.edu.co')) {
      await signOut(auth);
      throw new Error("Debe usar un correo que no sea institucional");
    }

    let role = tokenResult.claims.role;
    if (!tokenResult.claims.role || tokenResult.claims.role === "") {
      await setAttendantClaims({ uid: userCredential.user.uid });
      const newToken = await userCredential.user.getIdTokenResult(true);
      role = newToken.claims.role as string
    }

    const authUser = {
      ...userCredential.user,
      role: role
    } as unknown as AuthUser
    return authUser
  },

  signInWithGoogle: async (): Promise<AuthUser> => {
    const userCredential = await signInWithPopup(auth, googleProvider)
    const firebaseUser = userCredential.user

    if (!firebaseUser.email?.endsWith('@jecaro.co') && !firebaseUser.email?.endsWith('@jecaro.edu.co')) {
      await signOut(auth);
      throw new Error('Debes usar un correo institucional');
    }

    const tokenResult = await userCredential.user.getIdTokenResult(true);

    // Check if user document exists in Firestore
    const studentDocRef = doc(db, 'students', firebaseUser.uid)
    const studentDoc = await getDoc(studentDocRef)

    if (studentDoc.exists()) {
      await signOut(auth);
      throw new Error('Las cuentas de estudiantes no están habilitadas para iniciar sesión.');
    }

    ;
    let role = tokenResult.claims.role;

    // Se sincroniza contra Google Workspace en cada inicio de sesion institucional.
    // Antes solo se hacia cuando el claim `role` venia vacio, asi que un cambio de
    // unidad organizativa nunca llegaba al token y el rol viejo quedaba fijo.
    try {
      await setupInstitutionalUser({ email: firebaseUser.email || '' });
      const newToken = await firebaseUser.getIdTokenResult(true);
      role = newToken.claims.role as string
    } catch (error) {
      // Si Workspace no responde se conserva el claim vigente. Solo se corta el
      // inicio de sesion cuando ademas no hay rol previo, porque sin rol la sesion
      // no sirve para nada.
      if (!role) {
        await signOut(auth);
        throw error
      }
      console.error('No se pudo sincronizar el rol con Google Workspace:', error);
    }
    ;

    if (role && (role !== "attendant" && role!=="student" )) {
      // Check if user document exists in Firestore
      const userDocRef = doc(db, 'users', firebaseUser.uid)
      const userDoc = await getDoc(userDocRef)

      // If user doesn't exist in Firestore, create the document
      if (!userDoc.exists()) {
        const newUserData: any = {
          identifier: firebaseUser.uid,
          identifierType: 'Google',
          email: firebaseUser.email || '',
          phoneNumber: firebaseUser.phoneNumber || '',
          displayName: firebaseUser.displayName || '',
          site: '', // You may want to set a default site or get it from somewhere
          relatedStudents: [],
          createdAt: getColombianTimestamp(),
        }

        await setDoc(userDocRef, newUserData)
      }
    }

    const authUser = {
      ...firebaseUser,
      role: role
    } as unknown as AuthUser
    return authUser
  },

  signInAttendantWithGoogle: async (): Promise<AuthUser> => {
    const userCredential = await signInWithPopup(auth, googleProvider)
    const firebaseUser = userCredential.user

    const tokenResult = await firebaseUser.getIdTokenResult(true);

    if (firebaseUser.email?.endsWith('@jecaro.co') || firebaseUser.email?.endsWith('@jecaro.edu.co')) {
      await signOut(auth);
      throw new Error("Debe usar un correo que no sea institucional");
    }

    let role = tokenResult.claims.role;
    if (!tokenResult.claims.role || tokenResult.claims.role === "") {
      await setAttendantClaims({ uid: firebaseUser.uid });
      const newToken = await firebaseUser.getIdTokenResult(true);
      role = newToken.claims.role as string
    }

    // Check if attendant exists in attendants collection
    const attendantDocRef = doc(db, 'attendants', firebaseUser.uid)
    const attendantDoc = await getDoc(attendantDocRef)

    if (!attendantDoc.exists()) {
      await signOut(auth);
      throw new Error('No se encontró una cuenta de acudiente asociada a este correo de Google. Por favor regístrate primero.');
    }

    const authUser = {
      ...firebaseUser,
      role: role
    } as unknown as AuthUser
    return authUser
  },

  registerWithGoogle: async (userData: Omit<User, 'documentId' | 'email' | 'createdAt' | 'displayName'>): Promise<AuthUser> => {
    const userCredential = await signInWithPopup(auth, googleProvider)
    const firebaseUser = userCredential.user

    const tokenResult = await firebaseUser.getIdTokenResult(true);
  
     
    if (firebaseUser.email?.endsWith('@jecaro.co') || firebaseUser.email?.endsWith('@jecaro.edu.co')) {
      await signOut(auth);
      ;   
      throw new Error("Debe usar un correo que no sea institucional");
    }

    let role = tokenResult.claims.role;
    if (!tokenResult.claims.role || tokenResult.claims.role === "") {
      await setAttendantClaims({ uid: firebaseUser.uid });
      const newToken = await firebaseUser.getIdTokenResult(true);
      role = newToken.claims.role as string
    }

    // Check if user already exists in attendants collection
    const userDocRef = doc(db, 'attendants', firebaseUser.uid)
    const userDoc = await getDoc(userDocRef)

    if (userDoc.exists()) {
      await signOut(auth);
      throw new Error('Esta cuenta ya está registrada');
    }

    // Validate required fields
    if (!userData.identifier || !userData.identifierType) {
      await signOut(auth);
      throw new Error('Datos de identificación incompletos');
    }

    // Create user document in Firestore with the UID as document ID
    const newUserData: any = {
      identifier: userData.identifier,
      identifierType: userData.identifierType,
      email: firebaseUser.email || '',
      phoneNumber: userData.phoneNumber || '',
      displayName: firebaseUser.displayName || '',
      site: userData.site || '',
      relatedStudents: userData.relatedStudents || [],
      createdAt: getColombianTimestamp(),
    }

    await setDoc(userDocRef, newUserData)

    const authUser = {
      ...firebaseUser,
      role: role
    } as unknown as AuthUser
    return authUser
  },

  logout: async () => {
    await signOut(auth)
  },

  resetPassword: async (email: string) => {
    await sendPasswordResetEmail(auth, email)
  },

  getCurrentUser: (): FirebaseUser | null => {
    return auth.currentUser
  },

  updateUserProfile: async (displayName: string) => {
    if (auth.currentUser) {
      await updateProfile(auth.currentUser, { displayName })
    }
  },
}
